/*
SPDX-FileCopyrightText: 2025 Genome Research Ltd.

SPDX-License-Identifier: MIT
*/

import React from "react";
import { CentreContents } from "@tol/tol-ui";
import { Link } from "react-router-dom";
import "../../scss/legacy-tol.scss";

const LegacyDatabase: React.FC = () => {
  return (
    <div style={{ backgroundColor: "white", color: "black" }}>
      <CentreContents>
        <header className="header">
          <h1 className="header-h1">Tree of sex</h1>
          <h2 className="header-h2">
            a database of eukaryotic sex determination systems
          </h2>
          <nav className="nav">
            <Link className="link" to={"/legacy-home"}>
              Home{" "}
            </Link>
            <Link className="link" to={"/legacy-consortium"}>
              Consortium{" "}
            </Link>
            <span>Database</span>
            <Link className="link" to={"/legacy-publications"}>
              Publications{" "}
            </Link>
          </nav>
        </header>
        <main className="legacy-main">
          <p>
            <strong>The Tree of Sex Database</strong>
            <br />
            <br />
            The vast majority of eukaryotic organisms reproduce sexually, but
            the nature of the sexual system and the mechanism of sex
            determination vary remarkably, even among closely related species.
            Testing evolutionary hypotheses regarding the causes and
            consequences of this diversity requires interspecific data placed in
            a phylogenetic context. In{" "}
            <a
              className="link"
              href="http://www.nature.com/articles/sdata201415"
              target="_blank"
            >
              The Tree of Sex Consortium (2014)
            </a>
            , we describe a database listing sexual systems and sex
            determination mechanisms across the eukaryotic tree of life, with
            data on sexual systems from 11,038 plant, 709 fish, 173 amphibian,
            593 non-avian reptilian, 195 avian, 479 mammalian, and 9,108
            invertebrate species.
          </p>
          <p>
            TraitDB was a searchable MySQL-based database, custom built by
            NESCent. It has been transitioned to the current R based shiny
            server, which allows authorized administrators to upload additional
            data and delete incorrect records. To submit additional data, please
            fill in a sample data form (for{" "}
            <a
              className="link"
              href="/resources/template.plant.csv"
              title="Plants Upload Template"
            >
              plants
            </a>{" "}
            or{" "}
            <a
              className="link"
              href="/resources/template.animal.csv"
              title="Animals Upload Template"
            >
              animals
            </a>
            ) and email to{" "}
            <a className="link" href="mailto:contact@treeofsex.org">
              contact@treeofsex.org
            </a>
            . Contributors who wish to obtain administrator status to help
            curate or upload data over longer periods of time should email{" "}
            <a className="link" href="mailto:contact@treeofsex.org">
              contact@treeofsex.org
            </a>{" "}
            with a CV and short description of their goals for the database.
          </p>

          <p>
            <strong>
              <a
                className="link"
                href="https://evobir.shinyapps.io/tos-plant/"
                target="_blank"
              >
                Plant database
              </a>
              <br />
              <a
                className="link"
                href="https://evobir.shinyapps.io/tos-vert/"
                target="_blank"
              >
                Vertebrate database
              </a>
              <br />
              <a
                className="link"
                href="https://evobir.shinyapps.io/tos-invert/"
                target="_blank"
              >
                Invertebrate database
              </a>
              <br />
              <br />
            </strong>
          </p>

          <p>
            Expanded datasets focused on chromosome number and sex chromosome
            systems are available for amphibians and the insect groups
            polyneoptera and coleoptera at{" "}
            <a className="link" href="http://www.karyotype.org" target="_blank">
              karyotype.org
            </a>
            .
          </p>
          <p>
            Download the May 19, 2014 database reported in{" "}
            <a
              className="link"
              href="http://www.nature.com/articles/sdata201415"
              target="_blank"
            >
              The Tree of Sex Consortium (2014)
            </a>
            :
          </p>
          <ul>
            <li>
              <a
                className="link"
                href="/resources/data.vert.csv"
                title="Vertebrate DataBase"
              >
                Vertebrates
              </a>
            </li>
            <li>
              <a
                className="link"
                href="/resources/data.invert.csv"
                title="Invertebrate DataBase"
              >
                Invertebrates
              </a>
            </li>
            <li>
              <a
                className="link"
                href="/resources/data.plant.csv"
                title="Plants DataBase"
              >
                Plants-data
              </a>
            </li>
            <li>
              <a
                className="link"
                href="/resources/data.plant.refs.csv"
                title="Plants Data Refs"
              >
                Plants-refs
              </a>
            </li>
          </ul>

          <div className="imgs">
            <img alt="Pasted Graphic" src="/legacy/vert.jpg" />
          </div>
          <div className="imgs">
            <img alt="Pasted Graphic 1" src="/legacy/invert.jpg" />
            <img alt="Pasted Graphic 2" src="/legacy/plant.jpg" />
          </div>
        </main>
        <footer className="legacy-footer">
          <p>
            &copy; 2014 Tree of Sex Consortium :{" "}
            <Link to={"/legacy-cookies-policy"}>Cookies Policy</Link>
          </p>
        </footer>
      </CentreContents>
    </div>
  );
};
export default LegacyDatabase;
